/**
 * RegenerateCodesDialog — verify TOTP, mint fresh recovery codes,
 * display ONCE for user to save.
 *
 * Path A 2FA Phase 2 (2026-05-26).
 *
 * 2 phases:
 *   1. Confirm prompt + TOTP entry → call regenerateRecoveryCodes()
 *   2. Display new codes with download / copy options
 *
 * Old codes invalidated server-side on success.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { twoFactorApi } from '../../api/twofa';
import { ApiError } from '../../api/client';
import { isStepUpCancelled } from '../../api/stepup';
import { translateApiError } from '../../i18n/translateApiError';

function downloadRecoveryCodes(codes: string[]) {
  const header = [
    'SVDY recovery codes (regenerated)',
    `Generated: ${new Date().toISOString()}`,
    '',
    'Each code can be used ONCE. Older recovery codes are now invalid.',
    'Keep these somewhere safe — for example, a password manager.',
    '',
    '----------------------------------------',
    '',
  ].join('\n');
  const body = codes.join('\n') + '\n';
  const blob = new Blob([header + body], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `svdy-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Props {
  open: boolean;
  onClose: () => void;
  onRegenerated: () => void;
}

const RegenerateCodesDialog: React.FC<Props> = ({
  open,
  onClose,
  onRegenerated,
}) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'confirm' | 'display'>('confirm');
  const [code, setCode] = useState('');
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleClose = () => {
    if (loading) return;
    onClose();
    setTimeout(() => {
      setPhase('confirm');
      setCode('');
      setNewCodes([]);
      setError(null);
      setAcknowledged(false);
    }, 200);
  };

  const handleRegenerate = async () => {
    if (code.length !== 6) {
      setError(t('common:security.enter6DigitCode'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await twoFactorApi.regenerateRecoveryCodes(code);
      setNewCodes(res.recovery_codes);
      setPhase('display');
      onRegenerated();
    } catch (e) {
      // The user declined the password prompt. Not a failure — rendering the
      // step-up demand here would tell them to do the thing they just refused
      // (owner report 2026-08-01, where it surfaced as the raw code).
      if (isStepUpCancelled(e)) return;
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
      );
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {phase === 'confirm' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>{t('common:twoFactor.regen.confirmTitle')}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('common:twoFactor.regen.confirmWarning')}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('common:twoFactor.regen.confirmBody')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label={t('common:security.sixDigitCode')}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.length === 6 && !loading) {
                  handleRegenerate();
                }
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 6,
                style: {
                  fontFamily: 'monospace',
                  fontSize: '1.25rem',
                  letterSpacing: '0.5rem',
                  textAlign: 'center',
                },
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button onClick={handleClose} disabled={loading}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleRegenerate}
              disabled={loading || code.length !== 6}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? t('common:twoFactor.regen.generating') : t('common:twoFactor.regen.confirmButton')}
            </Button>
          </DialogActions>
        </>
      )}

      {phase === 'display' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>{t('common:twoFactor.regen.displayTitle')}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('common:twoFactor.regen.displayWarning')}
            </Alert>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                p: 2,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.95rem',
                letterSpacing: '0.05em',
                mb: 2,
              }}
            >
              {newCodes.map((c, idx) => (
                <Box key={c} sx={{ color: 'text.primary' }}>
                  <Box component="span" sx={{ color: 'text.disabled', mr: 1 }}>
                    {String(idx + 1).padStart(2, '0')}.
                  </Box>
                  {c}
                </Box>
              ))}
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => downloadRecoveryCodes(newCodes)}
              >
                {t('common:twoFactor.setup.downloadTxt')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigator.clipboard?.writeText(newCodes.join('\n'))
                }
              >
                {t('common:twoFactor.setup.copyAll')}
              </Button>
            </Stack>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                userSelect: 'none',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              {t('common:twoFactor.regen.acknowledge')}
            </Box>
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button
              variant="contained"
              onClick={handleClose}
              disabled={!acknowledged}
            >
              {t('common:twoFactor.setup.done')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default RegenerateCodesDialog;
